/**
 * Theme configuration constants
 * Contains theme keys, display names, file paths, and color presets
 */

export type ThemeKey = "light" | "dark" | "cobalt" | "nature";
export type ColorScheme = "light" | "dark";

export interface ThemeColorPreset {
  primary: string;
  secondary: string;
  accent: string;
}

export interface ThemeDefinition {
  key: ThemeKey;
  label: string;
  cssFile: string;
  colorScheme: ColorScheme;
  colors: ThemeColorPreset;
}

// Theme file paths mapping
export const THEME_CSS_FILES: Record<ThemeKey, string> = {
  "light": "/themes/light.css",
  "dark": "/themes/dark.css",
  "cobalt": "/themes/cobalt.css",
  "nature": "/themes/nature.css",
} as const;

// Theme definitions with colors for UI display
export const THEME_DEFINITIONS: Record<ThemeKey, ThemeDefinition> = {
  "light": {
    key: "light",
    label: "Light",
    cssFile: THEME_CSS_FILES["light"],
    colorScheme: "light",
    colors: {
      primary: "hsl(207 77% 51%)",
      secondary: "hsl(0 0% 96%)",
      accent: "hsl(0 0% 96%)",
    },
  },
  "dark": {
    key: "dark",
    label: "Dark",
    cssFile: THEME_CSS_FILES["dark"],
    colorScheme: "dark",
    colors: {
      primary: "hsl(207 77% 51%)",
      secondary: "hsl(0 0% 22%)",
      accent: "hsl(0 0% 22%)",
    },
  },
  "cobalt": {
    key: "cobalt",
    label: "Cobalt",
    cssFile: THEME_CSS_FILES["cobalt"],
    colorScheme: "dark",
    colors: {
      primary: "hsl(217 91% 60%)",
      secondary: "hsl(235 24% 20%)",
      accent: "hsl(220 48% 22%)",
    },
  },
  "nature": {
    key: "nature",
    label: "Nature",
    cssFile: THEME_CSS_FILES["nature"],
    colorScheme: "dark",
    colors: {
      primary: "hsl(122.4242 39.4422% 49.2157%)",
      secondary: "hsl(115.3846 9.6296% 26.4706%)",
      accent: "hsl(122.7907 43.4343% 38.8235%)",
    },
  },
} as const;

// Available themes array for iteration
export const AVAILABLE_THEMES: ReadonlyArray<ThemeDefinition> = Object.values(THEME_DEFINITIONS);

// Default theme configuration
export const DEFAULT_THEME_CONFIG = {
  theme: "light" as ThemeKey,
} as const;

// LocalStorage key for theme configuration
export const THEME_STORAGE_KEY = "app-theme-config";